import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BadgeCheck, Loader2 } from "lucide-react";
import { http, SEGMENTS, errMsg } from "@/lib/data";

const field = "w-full h-12 rounded-xl bg-white/5 border border-white/15 px-4 text-base text-white placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-[#10B981]";
const EMPTY = { full_name: "", entity_name: "", contact_mobile: "", operating_segment: "", registration_number: "" };

export const AffiliateForm = () => {
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState({ loading: false, error: "", done: false });
  const seg = SEGMENTS.find((s) => s.value === f.operating_segment);
  const set = (k) => (e) => setF({ ...f, [k]: k === "contact_mobile" ? e.target.value.replace(/\D/g, "") : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.operating_segment) return setState((s) => ({ ...s, error: "Please select your operating segment" }));
    setState({ loading: true, error: "", done: false });
    try {
      await http.post("/affiliates", f);
      setState({ loading: false, error: "", done: true });
      setF(EMPTY);
    } catch (err) {
      setState({ loading: false, error: errMsg(err), done: false });
    }
  };

  return (
    <section id="join-partner" data-testid="b2b-section" className="relative grain overflow-hidden scroll-mt-48 bg-[#1E293B] rounded-3xl p-6 sm:p-10 text-white mt-16">
      <div className="relative grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-300">For Financial Partners</p>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-2 leading-tight">Join DhanTrove as a <span className="gold-text">Verified Partner</span></h2>
          <p className="text-slate-300 text-sm sm:text-base mt-3 leading-relaxed">Reach intent-ready retail investors across India. Every partner is verified by our compliance desk before listing.</p>
        </div>
        <div className="lg:col-span-3">
          {state.done ? (
            <div data-testid="b2b-success" className="rounded-2xl bg-[#10B981]/15 border border-[#10B981]/40 p-6 sm:p-8">
              <BadgeCheck className="w-10 h-10 text-[#10B981]" />
              <p className="font-display text-xl font-bold mt-3">Application Received.</p>
              <p className="text-slate-200 mt-1">Our Verification Desk will contact you within 48 Hours.</p>
              <button data-testid="b2b-new-button" onClick={() => setState({ loading: false, error: "", done: false })} className="mt-5 text-sm font-semibold text-[#10B981] hover:underline">Submit another application</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
              <input data-testid="b2b-name-input" required minLength={2} placeholder="Full Professional Name" value={f.full_name} onChange={set("full_name")} className={field} />
              <input data-testid="b2b-entity-input" required minLength={2} placeholder="Entity Name" value={f.entity_name} onChange={set("entity_name")} className={field} />
              <input data-testid="b2b-mobile-input" required inputMode="numeric" maxLength={10} placeholder="Contact Mobile (10 digits)" value={f.contact_mobile} onChange={set("contact_mobile")} className={field} />
              <Select value={f.operating_segment} onValueChange={(v) => setF({ ...f, operating_segment: v })}>
                <SelectTrigger data-testid="b2b-segment-select" className="h-12 rounded-xl bg-white/5 border-white/15 text-white text-base"><SelectValue placeholder="Operating Segment" /></SelectTrigger>
                <SelectContent>
                  {SEGMENTS.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`b2b-segment-option-${s.value}`}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-amber-200">{seg ? `Enter active ${seg.reg}` : "Enter active RERA Code / AMFI ARN / SEBI Registration Number"}</label>
                <input data-testid="b2b-regulatory-input" required minLength={4} placeholder={seg ? seg.reg : "RERA Code / AMFI ARN / SEBI Registration Number"} value={f.registration_number} onChange={set("registration_number")} className={`${field} mt-1.5`} />
              </div>
              {state.error && <p data-testid="b2b-error" className="sm:col-span-2 text-sm text-red-300">{state.error}</p>}
              <button data-testid="b2b-submit-button" disabled={state.loading} className="sm:col-span-2 h-12 rounded-xl bg-[#10B981] hover:bg-[#059669] font-bold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-70">
                {state.loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit Partner Application
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
